import styled from "@emotion/styled";
import { useEffect, useState } from "react";

const TextDiv = styled.div([
    props => props.theme.typography.medium,
    props => props.theme.colors.foreground.secondary,
]);

const AnimatedTextDiv = styled(TextDiv)([
    {
        opacity: 0,
    },
    `
        @keyframes fadeIn {
            from {
                opacity: 0.0
            }
            to {
                opacity: 1.0
            }
        }

        @keyframes blinking {
            from {
                opacity: 1.0
            }
            to {
                opacity: 0.6
            }
        }

        @keyframes fadeOut {
            from {
                opacity: 1.0
            }
            to {
                opacity: 0.0
            }
        }       

        animation-name: fadeIn, blinking, fadeOut;
        animation-delay: 0s, 0.25s, 3.25s;
        animation-duration: 0.25s, 0.5s, 0.2s;
        animation-timing-function: ease-out, linear, cubic-bezier(0.76, 0.00, 0.24, 1.0);
        animation-iteration-count: 1, 6, 1;
        animation-direction: normal, alternate, normal;
    `
]);

type AnimatedTextStage = {
    visible: false;
} | {
    visible: true;
    currentIndex: number;
}

interface AnimatedTextProps {
    messages: string[];
}

const AnimatedText: React.FC<AnimatedTextProps> = ({ messages }) => {

    const useManyMessages = messages.length > 1;

    const [stage, setStage] = useState<AnimatedTextStage>(useManyMessages ? { visible: false } : { visible: true, currentIndex: 0 });

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setStage(prevStage => {
                if (prevStage.visible) {
                    return { visible: true, currentIndex: (prevStage.currentIndex + 1) % messages.length };
                } else {
                    return { visible: true, currentIndex: 0 };
                }
            });
        }, 4 * 1e3);

        return function tearDown() {
            window.clearTimeout(intervalId);
        }
    }, []);

    return (
        <>
            {stage.visible && (
                useManyMessages ? (
                    <AnimatedTextDiv key={stage.currentIndex} data-cy="animated-text">{messages[stage.currentIndex]}</AnimatedTextDiv>
                ) : (
                    <TextDiv data-cy="animated-text">{messages[stage.currentIndex]}</TextDiv>
                )
            )}
        </>
    );
}

export default AnimatedText;